import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { timeLogAPI, timerAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { cn, formatDate, formatDuration, formatTimer } from '../lib/utils';
import {
    Clock,
    Play,
    Square,
    Calendar as CalendarIcon,
    Download,
    Filter,
    DollarSign,
    Loader2,
    Timer,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, startOfWeek, endOfWeek } from 'date-fns';
import { useTimerContext } from '@/context/TimerContext';

export default function TimeTrackingPage() {
    const { currentWorkspace, tasks } = useWorkspace();
    const [timeLogs, setTimeLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const {activeTimer, setActiveTimer, checkActiveTimer} = useTimerContext();
    const [timerSeconds, setTimerSeconds] = useState(0);
    
    // Filters
    const [dateRange, setDateRange] = useState('week');
    const [startDate, setStartDate] = useState(startOfWeek(new Date()));
    const [endDate, setEndDate] = useState(endOfWeek(new Date()));
    const [filterBillable, setFilterBillable] = useState('all');

    useEffect(() => {
        if (currentWorkspace) {
            fetchTimeLogs();
            // checkActiveTimer();
        }
    }, [currentWorkspace, startDate, endDate, filterBillable]);

    useEffect(() => {
        let interval;
        if (activeTimer?.is_running) {
            interval = setInterval(() => {
                const start = new Date(activeTimer.start_time);
                const now = new Date();
                const diffSeconds = Math.floor((now - start) / 1000);
                setTimerSeconds(diffSeconds);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [activeTimer]);

    const fetchTimeLogs = async () => {
        setLoading(true);
        try {
            const params = {
                start_date: format(startDate, 'yyyy-MM-dd'),
                end_date: format(endDate, 'yyyy-MM-dd'),
            };
            if (filterBillable !== 'all') {
                params.is_billable = filterBillable === 'billable';
            }
            const response = await timeLogAPI.getByWorkspace(currentWorkspace.id, params);
            setTimeLogs(response.data);
        } catch (error) {
            console.error('Failed to fetch time logs:', error);
        } finally {
            setLoading(false);
        }
    };


    const handleStartTimer = async (taskId) => {
        try {
            const response = await timerAPI.start(taskId);
            setActiveTimer(response.data);
            toast.success('Timer started');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start timer');
        }
    };

    const handleStopTimer = async () => {
        try {
            await timerAPI.stop();
            setActiveTimer(null);
            setTimerSeconds(0);
            toast.success('Timer stopped and time logged');
            fetchTimeLogs();
        } catch (error) {
            toast.error('Failed to stop timer');
        }
    };

    const handleDateRangeChange = (range) => {
        setDateRange(range);
        const now = new Date();
        switch (range) {
            case 'today':
                setStartDate(now);
                setEndDate(now);
                break;
            case 'week':
                setStartDate(startOfWeek(now));
                setEndDate(endOfWeek(now));
                break;
            case 'month':
                setStartDate(new Date(now.getFullYear(), now.getMonth(), 1));
                setEndDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
                break;
            default:
                break;
        }
    };

    const handleExport = () => {
        // Create CSV content
        const headers = ['Date', 'Task', 'Duration (min)', 'Description', 'Billable', 'Type'];
        const rows = timeLogs.map(log => {
            const task = tasks.find(t => t.id === log.task_id);
            return [
                log.logged_date,
                task?.title || 'Unknown',
                log.duration,
                log.description || '',
                log.is_billable ? 'Yes' : 'No',
                log.log_type,
            ];
        });
        
        const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `time-logs-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.csv`;
        a.click();
        toast.success('Time logs exported');
    };

    // Calculate stats
    const totalMinutes = timeLogs.reduce((sum, log) => sum + log.duration, 0);
    const billableMinutes = timeLogs.filter(l => l.is_billable).reduce((sum, log) => sum + log.duration, 0);
    const nonBillableMinutes = totalMinutes - billableMinutes;

    // Group by date
    const logsByDate = {};
    timeLogs.forEach(log => {
        if (!logsByDate[log.logged_date]) {
            logsByDate[log.logged_date] = [];
        }
        logsByDate[log.logged_date].push(log);
    });

    if (!currentWorkspace) {
        return (
            <Layout title="Time Tracking">
                <div className="empty-state">
                    <p className="text-brand-text-muted">Please select a workspace first</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Time Tracking" subtitle="Track and manage your time">
            <div className="space-y-6" data-testid="time-tracking-page">
                {/* Active Timer */}
                {activeTimer && (
                    <Card className="border-brand-primary bg-brand-accent-soft/30" data-testid="active-timer-card">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-brand-primary/10 rounded-full flex items-center justify-center">
                                        <Timer className="w-6 h-6 text-brand-primary animate-pulse" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <p className="text-sm text-brand-text-secondary">Timer running</p>
                                        <p className="text-3xl font-bold font-mono text-brand-primary">
                                            {formatTimer(timerSeconds)}
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    onClick={handleStopTimer}
                                    className="bg-brand-error hover:bg-brand-error/90"
                                    data-testid="stop-timer-btn"
                                >
                                    <Square className="w-4 h-4 mr-2" fill="currentColor" strokeWidth={1.5} />
                                    Stop Timer
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary mb-1">Total Time</p>
                                    <p className="stat-value">{formatDuration(totalMinutes)}</p>
                                </div>
                                <div className="w-10 h-10 bg-brand-accent-soft rounded-lg flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary mb-1">Billable</p>
                                    <p className="stat-value text-brand-success">{formatDuration(billableMinutes)}</p>
                                </div>
                                <div className="w-10 h-10 bg-brand-success/10 rounded-lg flex items-center justify-center">
                                    <DollarSign className="w-5 h-5 text-brand-success" strokeWidth={1.5} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary mb-1">Non-billable</p>
                                    <p className="stat-value text-brand-text-muted">{formatDuration(nonBillableMinutes)}</p>
                                </div>
                                <div className="w-10 h-10 bg-brand-background rounded-lg flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-brand-text-muted" strokeWidth={1.5} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Filters & Controls */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Select value={dateRange} onValueChange={handleDateRangeChange}>
                            <SelectTrigger className="w-32" data-testid="date-range-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="today">Today</SelectItem>
                                <SelectItem value="week">This Week</SelectItem>
                                <SelectItem value="month">This Month</SelectItem>
                                <SelectItem value="custom">Custom</SelectItem>
                            </SelectContent>
                        </Select>

                        <Select value={filterBillable} onValueChange={setFilterBillable}>
                            <SelectTrigger className="w-36" data-testid="billable-filter-select">
                                <Filter className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Time</SelectItem>
                                <SelectItem value="billable">Billable Only</SelectItem>
                                <SelectItem value="non-billable">Non-billable</SelectItem>
                            </SelectContent>
                        </Select>

                        {dateRange === 'custom' && (
                            <div className="flex items-center gap-2">
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" size="sm">
                                            <CalendarIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                            {format(startDate, 'MMM d')}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={startDate}
                                            onSelect={setStartDate}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                                <span className="text-brand-text-muted">to</span>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" size="sm">
                                            <CalendarIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                            {format(endDate, 'MMM d')}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={endDate}
                                            onSelect={setEndDate}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        )}
                    </div>

                    <Button variant="outline" onClick={handleExport} data-testid="export-btn">
                        <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Export CSV
                    </Button>
                </div>

                {/* Time Logs */}
                <Tabs defaultValue="logs" className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="logs">Time Logs</TabsTrigger>
                        <TabsTrigger value="start">Start Timer</TabsTrigger>
                    </TabsList>

                    <TabsContent value="logs">
                        {loading ? (
                            <div className="flex items-center justify-center h-64">
                                <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                            </div>
                        ) : Object.keys(logsByDate).length > 0 ? (
                            <div className="space-y-6">
                                {Object.entries(logsByDate)
                                    .sort(([a], [b]) => new Date(b) - new Date(a))
                                    .map(([date, logs]) => {
                                        const dayTotal = logs.reduce((sum, l) => sum + l.duration, 0);
                                        return (
                                            <div key={date}>
                                                <div className="flex items-center justify-between mb-3">
                                                    <h3 className="font-medium text-brand-text-primary">
                                                        {formatDate(date)}
                                                    </h3>
                                                    <span className="text-sm text-brand-text-muted">
                                                        {formatDuration(dayTotal)}
                                                    </span>
                                                </div>
                                                <Card className="border-brand-border">
                                                    <div className="divide-y divide-brand-border">
                                                        {logs.map(log => {
                                                            const task = tasks.find(t => t.id === log.task_id);
                                                            return (
                                                                <div 
                                                                    key={log.id}
                                                                    className="flex items-center gap-4 p-4"
                                                                    data-testid={`time-log-${log.id}`}
                                                                >
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="font-medium text-sm text-brand-text-primary truncate">
                                                                            {task?.title || 'Unknown Task'}
                                                                        </p>
                                                                        {log.description && (
                                                                            <p className="text-xs text-brand-text-muted truncate">
                                                                                {log.description}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex items-center gap-3">
                                                                        {log.is_billable && (
                                                                            <span className="text-xs px-2 py-0.5 bg-brand-success/10 text-brand-success rounded-full">
                                                                                Billable
                                                                            </span>
                                                                        )}
                                                                        <span className={cn(
                                                                            'text-xs px-2 py-0.5 rounded-full',
                                                                            log.log_type === 'timer' 
                                                                                ? 'bg-brand-info/10 text-brand-info' 
                                                                                : 'bg-brand-accent-soft text-brand-text-secondary'
                                                                        )}>
                                                                            {log.log_type}
                                                                        </span>
                                                                        <span className="font-mono text-sm font-medium text-brand-text-primary">
                                                                            {formatDuration(log.duration)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </Card>
                                            </div>
                                        );
                                    })}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <Clock className="empty-state-icon" strokeWidth={1.5} />
                                <h2 className="empty-state-title">No time logged</h2>
                                <p className="empty-state-description">
                                    Start tracking time on your tasks to see logs here.
                                </p>
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="start">
                        <Card className="border-brand-border">
                            <CardHeader>
                                <CardTitle className="text-lg font-heading">Start Timer on a Task</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {tasks.length > 0 ? (
                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                        {tasks.filter(t => t.status !== 'Done').slice(0, 20).map(task => (
                                            <div 
                                                key={task.id}
                                                className="flex items-center justify-between p-3 rounded-lg hover:bg-brand-accent-soft/50 transition-colors"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm truncate">{task.title}</p>
                                                    <p className="text-xs text-brand-text-muted">
                                                        #{task.task_number} • {task.status}
                                                    </p>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleStartTimer(task.id)}
                                                    disabled={!!activeTimer}
                                                    className="bg-brand-primary hover:bg-brand-primary-dark"
                                                    data-testid={`start-timer-task-${task.id}`}
                                                >
                                                    <Play className="w-4 h-4 mr-1" strokeWidth={1.5} />
                                                    Start
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-center text-brand-text-muted py-8">
                                        No tasks available. Create tasks first to track time.
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    );
}
